
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import {MDXRemote, MDXRemoteSerializeResult} from 'next-mdx-remote'
import {serialize} from 'next-mdx-remote/serialize'
import useTranslation from 'next-translate/useTranslation'
import {getPlaiceholder} from 'plaiceholder'
import {useState} from 'react'
import Masonry from 'react-masonry-css'
import Button from '../components/atoms/button'
import Container from '../components/atoms/container'
import GridContainer from '../components/atoms/grid-container'
import Heading from '../components/atoms/heading'
import HighlightedSection from '../components/atoms/highlighted-section'
import PlusIcon from '../components/atoms/icons/plus-icon'
import LoadableImage from '../components/atoms/loadable-image'
import Paragraph from '../components/atoms/paragraph'
import Avatar from '../components/molecules/avatar'
import CallToAction from '../components/molecules/call-to-action'
import DynamicLink from '../components/molecules/dynamic-link'
import InfoBox from '../components/molecules/info-box'
import TeamMemberCard from '../components/molecules/team-member-card'
import PageCover from '../components/organisms/page-cover'
import {FooterDataStateProps, FooterProvider} from '../contexts/footer-context'
import PageLayout from '../layouts/page-layout'
import type {AboutUsDataProps} from '../lib/fetch-about-us-data'
import fetchAboutUsData from '../lib/fetch-about-us-data'
import fetchGeneralSettings from '../lib/fetch-general-settings'
import fetchSocialLinks from '../lib/fetch-social-links'
import mapRawImageToImage from '../lib/map-raw-image-to-image'
import mapRawTeamMemberToTeamMember from '../lib/map-raw-team-member-to-team-member'
import bishopSymbol from '../public/assets/bishop-logo-symbol.png'

export default function AboutUs({
  coverPhoto,
  coverPhotoAlt,
  coverTitleUpper,
  coverTitleLower,
  coverDescription,
  metaDescription,
  previewImage,
  companyInfo,
  values,
  team,
  cityInfo,
  officePhotos,
  cityPhotos,
  social,
  general,
}: AboutUsDataProps<MDXRemoteSerializeResult> & FooterDataStateProps) {
  const {t} = useTranslation('about-us')
  const [teamMembersCollapsed, setTeamMembersCollapsed] = useState(true)
  const slicedTeamMembers = teamMembersCollapsed ? team.slice(0, 6) : team

  return (
    <FooterProvider value={{social, general}}>
      <PageLayout
        className="grid grid-flow-row gap-16 md:gap-24 pt-24 lg:pt-10 pb-12 md:pb-24"
        disableDefaultPadding
        title={t('common:navigation.about-us')}
        description={metaDescription}
        openGraph={{
          images: [{url: previewImage || bishopSymbol.src}],
          url: "about-us"
        }}
        prefixImageUrl={!previewImage}
      >
        <div>
          <PageCover
            className="sm:min-h-180"
            title={
              <Heading
                variant="h1"
                disableMaxWidth
                className="grid gap-0 md:gap-4 place-content-center lg:justify-items-start text-center lg:text-left lg:max-w-lg font-black"
              >
                <span className="md:text-4xl md:leading-tight lg:text-5xl">
                  {coverTitleUpper}
                </span>

                {coverTitleLower && (
                  <span className="md:text-4xl md:leading-tight lg:text-5xl">
                    {coverTitleLower}
                  </span>
                )}
              </Heading>
            }
            description={coverDescription}
            media={
              <LoadableImage
                src={coverPhoto}
                alt={coverPhotoAlt}
                layout="responsive"
                width={558}
                height={460}
                objectFit="cover"
                borderRadius="rounded-none sm:rounded-3xl"
                wrapperClassName="max-w-none sm:max-w-xl lg:max-w-none -mx-6 -mt-4 sm:mx-auto sm:mt-0"
                sizes="(max-width: 576px) 576px, (max-width: 640px) 828px, 640px"
                priority
              />
            }
          />

          <HighlightedSection>
            <Heading variant="h2" className="text-center">
              {t('company.title')}
            </Heading>

            <div className="grid gap-4">
              <MDXRemote
                {...companyInfo}
                components={{p: Paragraph, a: DynamicLink}}
              />
            </div>

            {officePhotos && officePhotos.length > 0 && (
              <Masonry
                breakpointCols={{768: 2, 1024: 3, default: 3}}
                className="masonry-grid"
                columnClassName="masonry-grid_column"
              >
                {officePhotos.map(({image, alt, width, height}) => (
                  <LoadableImage
                    src={image}
                    borderRadius="rounded-2xl md:rounded-3xl"
                    alt={alt}
                    key={image}
                    width={width}
                    height={height}
                  />
                ))}
              </Masonry>
            )}
          </HighlightedSection>
        </div>

        <Container
          component="section"
          className="grid gap-8 justify-items-center"
        >
          <Heading variant="h2" className="text-center">
            {t('values.title')}
          </Heading>

          <GridContainer className="md:grid-cols-3 gap-y-8 gap-x-12 w-full">
            {values.map(({title, body}) => (
              <InfoBox title={title} key={title}>
                {body}
              </InfoBox>
            ))}
          </GridContainer>
        </Container>

        <HighlightedSection>
          <Heading variant="h2" className="text-center">
            {t('team.title')}
          </Heading>

          <GridContainer
            disableDefaultColumns
            className="hidden sm:grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-y-8 gap-x-12 w-full items-start"
          >
            {team.map(({image, name, role}) => (
              <TeamMemberCard key={`${name}-${role}`} name={name} role={role}>
                <Avatar src={image} alt={`${name} / ${role}`} size={125} />
              </TeamMemberCard>
            ))}
          </GridContainer>

          <GridContainer
            disableDefaultColumns
            className="hidden xs-min-max:grid sm:hidden grid-cols-2 gap-y-8 gap-x-12 w-full"
          >
            {slicedTeamMembers.map(({image, name, role}) => (
              <TeamMemberCard key={`${name}-${role}`} name={name} role={role}>
                <Avatar src={image} alt={`${name} / ${role}`} size={125} />
              </TeamMemberCard>
            ))}
          </GridContainer>

          <GridContainer
            disableDefaultColumns
            className="grid xs:hidden grid-cols-1 gap-y-8 gap-x-12 w-full"
          >
            {slicedTeamMembers.map(({image, name, role}) => (
              <TeamMemberCard key={`${name}-${role}`} name={name} role={role}>
                <Avatar src={image} alt={`${name} / ${role}`} size={125} />
              </TeamMemberCard>
            ))}
          </GridContainer>

          {teamMembersCollapsed && (
            <Button
              className="grid grid-flow-col justify-items-start gap-x-2 sm:hidden justify-self-center"
              variant="secondary"
              onClick={() => setTeamMembersCollapsed(false)}
            >
              <PlusIcon />

              <span>{t('common.see-all')}</span>
            </Button>
          )}
        </HighlightedSection>

        <Container className="grid gap-8 justify-items-center">
          <Heading variant="h2" className="text-center">
            {t('city.title')}
          </Heading>

          <GridContainer className="md:grid-cols-3 gap-y-8 gap-x-12 w-full">
            {cityInfo.map(({title, body}) => (
              <InfoBox title={title} key={title}>
                {body}
              </InfoBox>
            ))}
          </GridContainer>

          {cityPhotos && cityPhotos.length > 0 && (
            <Masonry
              breakpointCols={{768: 2, 1024: 3, default: 3}}
              className="masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {cityPhotos.map(({image, alt, width, height}) => (
                <LoadableImage
                  src={image}
                  className="rounded-2xl md:rounded-3xl"
                  alt={alt}
                  key={image}
                  width={width}
                  height={height}
                />
              ))}
            </Masonry>
          )}
        </Container>

        <Container>
          <CallToAction
            title={t('contact.title')}
            callToActionText={t('common:navigation.contact-us')}
            href="/contact-us"
          >
            {t('contact.body')}
          </CallToAction>
        </Container>
      </PageLayout>
    </FooterProvider>
  )
}

 async function _getStaticProps() {
  const {
    coverPhoto,
    coverPhotoAlt,
    coverTitleUpper,
    coverTitleLower,
    coverDescription: rawCoverDescription,
    metaDescription,
    previewImage,
    companyInfo: rawCompanyInfo,
    values,
    team,
    cityInfo,
    officePhotos,
    cityPhotos,
  } = fetchAboutUsData()

  const convertedCityPhotos = await Promise.all(
    cityPhotos.map(({image}) => getPlaiceholder(image)),
  )

  const convertedOfficePhotos = await Promise.all(
    officePhotos.map(({image}) => getPlaiceholder(image)),
  )

  const coverDescription = await serialize(rawCoverDescription)
  const companyInfo = await serialize(rawCompanyInfo)

  return {
    props: {
      coverPhoto,
      coverPhotoAlt,
      coverTitleUpper,
      coverTitleLower,
      coverDescription,
      metaDescription,
      previewImage,
      companyInfo,
      values,
      team: team.map(mapRawTeamMemberToTeamMember),
      cityInfo,
      officePhotos: convertedOfficePhotos.map(mapRawImageToImage(officePhotos)),
      cityPhotos: convertedCityPhotos.map(mapRawImageToImage(cityPhotos)),
      social: fetchSocialLinks(),
      general: fetchGeneralSettings(),
    },
  }
}


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/about-us',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  